import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightFromBracket,
  faUser,
  faEyeSlash,
  faEye,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { Switch } from "../../../components/ui/Switch";
import { Link } from "react-router-dom";
import { NavigationBarButton } from "./NavigationBarButton";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { BaseMenu } from "../../../components/Cart/Cart-Icon/BaseMenu";
import { useAdminImpersonationDialog } from "../hooks/useAdminImpersonationDialog";
import { usePriceVisibility } from "../../../../contexts/PriceVisibilityContext";
import { useCartHidden } from "../../../../hooks/useCartHidden";

interface LoggedInMenu {
  logout: () => void;
}

export function LoggedInMenu({ logout }: LoggedInMenu) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { profile, role } = useProfileContext();
  const {
    hidePrices,
    showCustomerPrices,
    togglePriceVisibility,
    toggleCustomerPrices,
  } = usePriceVisibility();
  const cartHidden = useCartHidden();
  const hasPriceIndicators =
    !cartHidden &&
    (hidePrices || (role === Role.Vendor && showCustomerPrices));
  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  const { isImpersonatingAdmin } = useProfileContext();

  const { component, open: openImpersonation } = useAdminImpersonationDialog({
    onContinue: async () => {},
  });

  function handleClose() {
    setAnchorEl(null);
  }

  function handleLogOut() {
    handleClose();
    logout();
  }

  function getUserInfo() {
    switch (role!) {
      case Role.Admin:
        return "JetSport Admin";
      case Role.Vendor:
        return /* profile?.userOrganizationInformations.organizationName ?? */ "Profile";
      case Role.EndUser:
        return /* profile?.userOrganizationInformations.organizationName ?? */ "Profile";
    }
    handleClose();
    logout();
  }

  return (
    <div>
      {component}
      <NavigationBarButton
        className={"!w-[60px] relative"}
        onClick={handleClick}
      >
        {hasPriceIndicators && (
          <div
            className={
              "h-[7px] w-[7px] rounded-full bg-success absolute top-5 right-5"
            }
          />
        )}
        {<FontAwesomeIcon size={"lg"} icon={faUser} />}
      </NavigationBarButton>
      <BaseMenu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Link to={"/account/me"}>
          <MenuItem onClick={handleClose}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faUser} />
            </ListItemIcon>
            <ListItemText>{getUserInfo()}</ListItemText>
          </MenuItem>
        </Link>
        {!cartHidden && (
          <MenuItem onClick={togglePriceVisibility}>
            <ListItemIcon>
              <FontAwesomeIcon icon={hidePrices ? faEyeSlash : faEye} />
            </ListItemIcon>
            <ListItemText className="mr-4">Hide Prices</ListItemText>
            <Switch
              checked={hidePrices}
              onCheckedChange={togglePriceVisibility}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {(role === Role.Vendor || role === Role.Admin) && (
          <MenuItem onClick={toggleCustomerPrices}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faDollarSign} />
            </ListItemIcon>
            <ListItemText className="mr-4">Show Customer Prices</ListItemText>
            <Switch
              checked={showCustomerPrices}
              onCheckedChange={toggleCustomerPrices}
              onClick={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}

        {/* {isImpersonatingAdmin && (
          <MenuItem onClick={() => openImpersonation()}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faUser} />
            </ListItemIcon>
            <ListItemText>Impersonation</ListItemText>
          </MenuItem>
        )} */}
        <MenuItem onClick={handleLogOut}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faRightFromBracket} />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </BaseMenu>
    </div>
  );
}
